/* ═══════════════════════════════════════════════════════════════════
   [D-458] «🌐 Кабінет» партнера в дашборді — чотири розділи-кнопки замість
   однієї довгої сторінки (OWNER 06.10: «немає чітких поділень, все в одній
   каші… ніколи, ніде не роби списками вниз»).

   🔗 Посилання · 🔑 Доступ · 👀 Режим учня · 📜 Журнал
   Однотипне — картками в сітці; пояснення — згорнуте «ℹ️ Як це працює».
   ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, TextField, Switch, Chip, Collapse } from '@mui/material';
import api from '../api';

const C = {
  card: '#16202c', card2: '#1b2836', line: 'rgba(0,229,160,.22)', txt: '#eaf6ff', dim: '#a8bece',
  neon: '#00e5a0', neonTxt: '#7ffcd3', coral: '#ff7a59', warn: '#ffb74d', blue: '#4fc3f7',
};
const fieldSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line } };
const btnMain = { bgcolor: C.neon, color: '#04121a', textTransform: 'none', fontWeight: 700,
                  '&:hover': { bgcolor: '#00c98c' }, '&.Mui-disabled': { bgcolor: 'rgba(0,229,160,.18)', color: C.neonTxt } };
const btnText = (c) => ({ textTransform: 'none', color: c, fontSize: 12.5, minWidth: 0 });
const grid = (min) => ({ display: 'grid', gap: 1.2, gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))` });
const fdt = (v) => (v ? new Date(v).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const fday = (v) => (v ? new Date(v).toLocaleDateString('uk-UA') : '—');

const Card = ({ children, color, sx }) => (
  <Box sx={{ bgcolor: C.card2, borderRadius: 2.5, p: 1.6, border: '1px solid rgba(255,255,255,.05)',
             borderTop: `3px solid ${color || C.line}`, display: 'flex', flexDirection: 'column', gap: .6, ...(sx || {}) }}>
    {children}
  </Box>
);
const Title = ({ children }) => <Typography sx={{ color: C.txt, fontWeight: 700, fontSize: 14 }}>{children}</Typography>;
const Dim = ({ children, sx }) => <Typography sx={{ color: C.dim, fontSize: 12.5, ...(sx || {}) }}>{children}</Typography>;

function Info({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 1.5 }}>
      <Button size="small" onClick={() => setOpen(o => !o)} sx={btnText(C.dim)}>ℹ️ {open ? 'Сховати пояснення' : 'Як це працює'}</Button>
      <Collapse in={open}>
        <Box sx={{ mt: .5, p: 1.4, borderRadius: 2, bgcolor: 'rgba(255,255,255,.03)', color: C.dim, fontSize: 12.5, lineHeight: 1.6, maxWidth: 760 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

const ACT = {
  invite_created: '🎟 створено запрошення', invite_revoked: '🚫 запрошення скасовано', cabinet_linked: '✅ підключив кабінет',
  access_added: '➕ ви додали акаунт', access_claimed: '✅ уперше увійшов', access_revoked: '🔒 доступ прибрано',
  web_login: '🌐 увійшов з браузера', web_login_approved: '👍 підтвердив вхід з браузера', web_login_denied: '⚠️ ВІДХИЛИВ вхід з браузера',
  open: '👀 відкрив кабінет', logout: '🚪 вийшов', user_feature: '🧩 змінив функцію юзеру', user_unbound: '🔗 юзера відвʼязано',
  preview_on: '👀 режим учня ввімкнено', preview_off: '↩ режим учня вимкнено',
  sale_receipt: '🧾 квитанція', sale_approved: '✅ оплату підтверджено', sale_rejected: '❌ оплату відхилено',
  sale_cancelled: '↩ оплату скасовано', cost_receipt: '🧾 чек витрати',
};
// [D-764] OWNER 28.10 «треба розібрати пункт (у журналі партнера такі дії записані на вас)»: хто зробив дію - словами
const VIA = { owner: 'ви (власник)', tg: 'партнер (Telegram)', web: 'партнер (браузер)', partner: 'партнер', bot: 'бот', system: 'система', studio: 'Студія' };
const LOG_GROUPS = [
  ['all', 'Усе', null],
  ['enter', 'Входи', ['open', 'web_login', 'web_login_approved', 'web_login_denied', 'logout', 'cabinet_linked', 'access_claimed']],
  ['access', 'Доступ', ['access_added', 'access_revoked', 'access_claimed', 'invite_created', 'invite_revoked']],
  ['preview', 'Режим учня', ['preview_on', 'preview_off']],
  ['money', 'Гроші й юзери', ['sale_receipt', 'sale_approved', 'sale_rejected', 'sale_cancelled', 'cost_receipt', 'user_feature', 'user_unbound']],
];

export default function PartnerCabinetTab({ p, d, reload, onChanged, setToast, setErr }) {
  const pid = p.id;
  const [sub, setSub] = useState('links');
  const [acc, setAcc] = useState('');
  const [ask, setAsk] = useState(null);     // [D-553] акаунт уже учень ІНШОГО партнера — власник обирає, як бути
  const [qr, setQr] = useState(null);
  const [logf, setLogf] = useState('all');
  // [D-728] OWNER 25.10: «щоб так само міг у любий час переглянути інформацію роботи партнера як учень» - Telegram ID акаунта
  // партнера → його юзер у боті (users.id) → та сама сторінка, що в «Користувачах» (обробки, налаштування, витрати)
  const navigate = useNavigate();
  const [uidByTg, setUidByTg] = useState(null);
  useEffect(() => {
    if (sub !== 'access' || uidByTg) return;
    api.get('/users').then(r => setUidByTg(Object.fromEntries((r.data || []).map(u => [String(u.telegram_id), u.id])))).catch(() => setUidByTg({}));
  }, [sub, uidByTg]);
  const fail =(e) => { const dt = e && e.response && e.response.data && e.response.data.detail; setErr('Не вдалося' + (typeof dt === 'string' && dt ? ': ' + dt : '')); };
  const copy = (t, what) => { try { navigator.clipboard.writeText(t); setToast(what ? what + ' скопійовано' : 'Скопійовано'); } catch (e) { setErr('Не вийшло скопіювати'); } };

  useEffect(() => {
    let url = null;
    api.get(`/partners/${pid}/qr`, { responseType: 'blob' }).then(r => { url = URL.createObjectURL(r.data); setQr(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [pid]);

  const live = d ? d.access.filter(a => !a.revoked_at) : [];
  const claimed = live.filter(a => !a.pending);
  const inPreview = claimed.filter(a => a.preview_until);
  const name = (a) => (a.tg_username ? '@' + a.tg_username : 'ID ' + a.tg_id);

  /* [D-553] OWNER 13.10: «хочу створити нового партнера, але цей партнер буде юзер який є у іншого партнера. Як бути?»
     Сервер без вибору такий акаунт не додає (409 student_of) — показуємо, чий це учень і що зміниться, і дві кнопки. */
  const addAccess = (student) => {
    const how = typeof student === 'string' ? student : null;
    const account = (how && ask ? ask.acc : acc).trim();
    if (!account) return;
    api.post(`/partners/${pid}/access`, how ? { account, student: how } : { account })
      .then(r => {
        const dd = r.data || {};
        setAcc(''); setAsk(null);
        setToast((dd.pending ? 'Додано — закріпиться при першому вході партнера' : 'Додано — партнеру прийшла кнопка кабінету')
                 + (dd.unbound_from ? ` · з учнів «${dd.unbound_from}» забрано` : ''));
        reload(); if (dd.unbound_from && onChanged) onChanged();
      })
      .catch(e => {
        const dt = e && e.response && e.response.data && e.response.data.detail;
        if (e && e.response && e.response.status === 409 && dt && dt.code === 'student_of') { setAsk({ ...dt, acc: account }); return; }
        fail(e);
      });
  };
  const revokeAccess = (a) => {
    if (!window.confirm(`Прибрати доступ ${name(a)} до кабінету? Усі його сесії в браузері закриються.`)) return;
    api.post(`/partners/${pid}/access/${a.id}/revoke`).then(() => { setToast('Доступ прибрано'); reload(); }).catch(fail);
  };
  const revokeInvite = (iid) => api.post(`/partners/${pid}/invites/${iid}/revoke`).then(() => { setToast('Запрошення скасовано'); reload(); }).catch(fail);
  const setPreview = (a, mode, pkgId) => api.post(`/partners/${pid}/access/${a.id}/preview`, { mode, package_id: pkgId || null })
    .then(r => { setToast(mode === 'off' ? 'Знову партнер' : 'Режим учня до ' + fdt(r.data.until) + ' — підказка вже в боті'); reload(); })
    .catch(fail);
  const setAllowed = (v) => api.post(`/partners/${pid}`, { preview_allowed: v })
    .then(() => { setToast(v ? 'Партнер може вмикати режим сам' : 'Самостійне вмикання вимкнено'); reload(); onChanged && onChanged(); })
    .catch(fail);

  const msg = d ? (
    `Вітаю! Ось усе для роботи з ботом Media Унікалізатор:\n\n` +
    `👥 Посилання для ваших учнів (дайте його їм або QR):\n${d.student_link}\n\n` +
    `🤝 Ваш кабінет у Telegram (телефон):\n${d.partner_link}\n\n` +
    `💻 Ваш кабінет на компʼютері:\n${d.cabinet_url}\n(натисніть «Увійти» і підтвердіть у Telegram)\n\n` +
    `Кабінет відкривається лише з вашого Telegram-акаунта` + (live.length ? ` (${live.map(name).join(', ')}).` : '.')
  ) : '';

  const SUBS = [
    ['links', '🔗 Посилання', null],
    ['access', '🔑 Доступ', live.length],
    ['preview', '👀 Режим учня', inPreview.length ? 'увімк.' : null],
    ['log', '📜 Журнал', null],
  ];

  if (!d) return <Dim>Завантаження…</Dim>;

  // ── 🔗 Посилання ─────────────────────────────────────────────────────────
  const Links = () => (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <Button variant="contained" size="small" data-guide="copy-msg" sx={btnMain} onClick={() => copy(msg, 'Повідомлення')}>📋 Скопіювати готове повідомлення партнеру</Button>
        <Dim>усі три посилання + пояснення — одним текстом</Dim>
      </Box>
      <Box sx={grid(250)}>
        <Card color={C.neon}>
          <Title>👥 Для учнів — бот</Title>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            {qr && <img src={qr} alt="QR" width={84} height={84} style={{ borderRadius: 8, background: '#fff', padding: 4, flex: 'none' }} />}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: C.neonTxt, fontSize: 12.5, wordBreak: 'break-all' }}>{d.student_link}</Typography>
              <Box sx={{ display: 'flex', gap: .5, mt: .3 }}>
                <Button size="small" sx={btnText(C.neon)} onClick={() => copy(d.student_link, 'Посилання')}>копіювати</Button>
                {qr && <Button size="small" component="a" href={qr} download={`QR-${p.name}.png`} sx={btnText(C.blue)}>⬇ QR</Button>}
              </Box>
            </Box>
          </Box>
          <Dim>Учень тисне → бот → «Старт» → пакети й реквізити партнера.</Dim>
          {!p.active && <Dim sx={{ color: C.warn }}>⚠️ Партнер вимкнений — посилання поки нікого не закріпить.</Dim>}
        </Card>
        <Card color={C.blue}>
          <Title>🤝 Партнеру — кабінет у Telegram</Title>
          <Typography sx={{ color: C.neonTxt, fontSize: 12.5, wordBreak: 'break-all' }}>{d.partner_link}</Typography>
          <Box><Button size="small" sx={btnText(C.neon)} onClick={() => copy(d.partner_link, 'Посилання')}>копіювати</Button></Box>
          <Dim>Для телефона: бот одразу дає кнопку кабінету. Лише для акаунтів із «🔑 Доступ».</Dim>
        </Card>
        <Card color={C.warn}>
          <Title>💻 Партнеру — кабінет на компʼютері</Title>
          <Typography sx={{ color: C.neonTxt, fontSize: 12.5, wordBreak: 'break-all' }}>{d.cabinet_url}</Typography>
          <Box><Button size="small" sx={btnText(C.neon)} onClick={() => copy(d.cabinet_url, 'Адресу')}>копіювати</Button></Box>
          <Dim>«Увійти» → підтвердження в Telegram партнера → сесія на 7 днів.</Dim>
        </Card>
      </Box>
    </>
  );

  // ── 🔑 Доступ ────────────────────────────────────────────────────────────
  const Access = () => (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" data-guide="acc-in" placeholder="@нік або ID партнера" value={acc} onChange={e => setAcc(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addAccess(); }} inputProps={{ style: { color: C.txt } }} sx={{ width: 260, ...fieldSx }} />
        <Button variant="contained" size="small" data-guide="acc-add" sx={btnMain} disabled={!acc.trim()} onClick={() => addAccess()}>➕ Додати акаунт</Button>
      </Box>
      {ask && (
        <Box data-pc-ask="1" sx={{ mb: 1.5, p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,183,77,.08)', border: `1px solid ${C.warn}` }}>
          <Typography sx={{ color: C.warn, fontWeight: 800, fontSize: 14.5 }}>
            ⚠️ {ask.acc} зараз учень партнера «{ask.partner}»{ask.active ? ` — доступ оплачено до ${fday(ask.until)}` : ''}
          </Typography>
          <Typography sx={{ color: C.txt, fontSize: 13, mt: .6, lineHeight: 1.5 }}>
            Акаунт партнера в боті — це лише кабінет. Щойно ви його додасте, відео цей акаунт більше не обробляє
            {ask.active ? `, і оплачений у «${ask.partner}» доступ працювати перестане` : ''}. Побачити бота очима учня він зможе
            кнопкою «👀 Як учень» у кабінеті — уже як учень партнера «{p.name}».
          </Typography>
          <Box sx={{ ...grid(260), mt: 1.2 }}>
            <Button data-pc-ask-btn="unbind" variant="contained" size="small" sx={btnMain} onClick={() => addAccess('unbind')}>
              ✅ Зробити партнером і забрати з учнів «{ask.partner}»</Button>
            <Button data-pc-ask-btn="keep" variant="outlined" size="small" onClick={() => addAccess('keep')}
              sx={{ textTransform: 'none', fontWeight: 700, color: C.warn, borderColor: C.warn }}>
              Зробити партнером, у «{ask.partner}» лишити в списку</Button>
            <Button data-pc-ask-btn="cancel" size="small" sx={btnText(C.dim)} onClick={() => setAsk(null)}>Скасувати</Button>
          </Box>
          <Dim sx={{ mt: 1 }}>
            Має й далі працювати з відео як учень «{ask.partner}»? Тоді «Скасувати» і впишіть сюди ІНШИЙ його Telegram-акаунт:
            один акаунт лишиться учнем, другий буде партнером.
          </Dim>
        </Box>
      )}
      {live.length === 0 && <Dim>Поки нікого — кабінет нікому не відкривається.</Dim>}
      <Box sx={grid(240)}>
        {live.map(a => (
          <Card key={a.id} color={a.pending ? C.warn : C.neon}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Title>{name(a)}</Title>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" label={a.pending ? '⏳ ще не заходив' : '✅ підключено'}
                sx={{ height: 21, fontSize: 11, bgcolor: 'transparent', color: a.pending ? C.warn : C.neon, border: `1px solid ${a.pending ? C.warn : C.neon}` }} />
            </Box>
            {!a.pending && <Dim>{a.tg_username ? `ID ${a.tg_id} · ` : ''}{a.web_sessions ? `🌐 браузер: ${a.web_sessions}` : 'лише Telegram'}</Dim>}
            {a.preview_until && <Dim sx={{ color: C.blue }}>👀 зараз у режимі учня до {fdt(a.preview_until)}</Dim>}
            <Dim>додано {fday(a.created_at)}</Dim>
            {!a.pending && uidByTg && (uidByTg[String(a.tg_id)]   /* [D-728] робота партнера як учня - сторінка з «Користувачів» */
              ? <Button data-own-work="1" size="small" variant="contained" sx={btnMain} onClick={() => navigate(`/users/${uidByTg[String(a.tg_id)]}`)}>
                  🎬 Робота партнера як учня →</Button>
              : <Dim>🎬 Цей акаунт ще не заходив у бот як учень - обробок немає.</Dim>)}
            <Box><Button size="small" sx={btnText(C.coral)} onClick={() => revokeAccess(a)}>Прибрати доступ</Button></Box>
          </Card>
        ))}
      </Box>
      {d.invites.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Dim>Старі одноразові запрошення (більше не потрібні):</Dim>
          {d.invites.map(i => (
            <Chip key={i.id} size="small" label={`#${i.id} до ${fdt(i.expires_at)}`} onDelete={() => revokeInvite(i.id)}
              sx={{ color: C.dim, borderColor: C.line }} variant="outlined" />
          ))}
        </Box>
      )}
      <Info>
        Впишіть @нік партнера в Telegram або його числовий ID. Кабінет — і в Telegram, і в браузері — відкривається лише цим
        акаунтам. Перший вхід закріплює доступ за незмінним Telegram ID: партнер може змінити нік — доступ лишиться в нього, а
        хтось інший із тим самим ніком його не отримає. Не знаєте нік чи ID? Дайте партнеру посилання кабінету — бот покаже йому ID.
        Акаунт партнера бачить у боті лише кабінет; свій акаунт вписати партнером не можна. Якщо акаунт уже учень іншого
        партнера — дашборд спитає: забрати його з тих учнів чи лишити в списку.
      </Info>
    </>
  );

  // ── 👀 Режим учня ────────────────────────────────────────────────────────
  const Preview = () => (
    <>
      {claimed.length === 0 && <Dim>Режим учня вмикається для акаунта, який уже заходив у кабінет. Спершу додайте акаунт у «🔑 Доступ».</Dim>}
      <Box sx={grid(280)}>
        {claimed.map(a => (
          <Card key={a.id} color={a.preview_until ? C.blue : C.line}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Title>{name(a)}</Title>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" label={a.preview_until ? '👀 учень' : '🤝 партнер'}
                sx={{ height: 21, fontSize: 11, bgcolor: a.preview_until ? 'rgba(79,195,247,.15)' : 'rgba(0,229,160,.1)', color: a.preview_until ? C.blue : C.neon }} />
            </Box>
            {a.preview_until ? (<>
              <Dim>Бачить бота й Хаб як учень до <b style={{ color: C.txt }}>{fdt(a.preview_until)}</b></Dim>
              <Box><Button size="small" variant="outlined" onClick={() => setPreview(a, 'off')}
                sx={{ textTransform: 'none', borderColor: C.warn, color: C.warn }}>↩ Повернути партнера</Button></Box>
            </>) : (<>
              <Dim>Показати цьому акаунту бота очима учня (на 3 години):</Dim>
              <Box sx={{ display: 'flex', gap: .8, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" data-guide="preview-nopay" onClick={() => setPreview(a, 'nopay')}
                  sx={{ textTransform: 'none', borderColor: C.blue, color: C.blue }}>🆕 новий, без оплати</Button>
                {(d.packages || []).map(k => (
                  <Button key={k.id} size="small" variant="outlined" onClick={() => setPreview(a, 'pkg', k.id)}
                    sx={{ textTransform: 'none', borderColor: C.blue, color: C.blue }}>📦 з пакетом «{k.name}»</Button>
                ))}
              </Box>
            </>)}
          </Card>
        ))}
        <Card color={d.preview_allowed ? C.warn : C.line}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Title>Партнер вмикає сам</Title>
            <Box sx={{ flex: 1 }} />
            <Switch size="small" checked={!!d.preview_allowed} onChange={e => setAllowed(e.target.checked)} />
          </Box>
          <Dim>{d.preview_allowed ? 'У кабінеті партнера є вкладка «👀 Як учень».' : 'Лише ви вмикаєте режим звідси.'}</Dim>
          <Dim sx={{ color: C.warn }}>Поки режим із пакетом увімкнено, партнер користується ботом безкоштовно.</Dim>
        </Card>
      </Box>
      <Info>
        «Новий, без оплати» — те, що бачить людина, яка щойно відкрила посилання: привітання, пакети, реквізити. «З пакетом» —
        учень, що вже заплатив: його меню, ліміт і лише функції пакета. Квитанції в режимі не зараховуються, у фінансах нічого не
        зʼявляється. Вийти: тут, у боті /partner → «↩ Вийти з режиму учня», або режим вимкнеться сам через 3 години —
        акаунт повертається точно таким, яким був.
      </Info>
    </>
  );

  // ── 📜 Журнал ────────────────────────────────────────────────────────────
  const Log = () => {
    const grp = LOG_GROUPS.find(g => g[0] === logf);
    const rows = d.audit.filter(e => !grp[2] || grp[2].includes(e.action));
    return (
      <>
        <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap', mb: 1.2 }}>
          {LOG_GROUPS.map(([k, l]) => (
            <Chip key={k} label={l} size="small" onClick={() => setLogf(k)}
              sx={{ cursor: 'pointer', bgcolor: logf === k ? 'rgba(0,229,160,.18)' : 'transparent', color: logf === k ? C.neon : C.dim,
                    border: `1px solid ${logf === k ? C.neon : 'rgba(255,255,255,.12)'}` }} />
          ))}
        </Box>
        {rows.length === 0 && <Dim>Подій немає.</Dim>}
        <Box sx={{ maxHeight: 360, overflowY: 'auto', borderRadius: 2, bgcolor: 'rgba(255,255,255,.02)' }}>
          {rows.map((e, k) => (
            <Box key={k} sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1, px: 1.2, py: .7,
                               borderBottom: '1px solid rgba(255,255,255,.04)' }}>
              <Typography sx={{ color: C.dim, fontSize: 12 }}>{fdt(e.created_at)}</Typography>
              <Typography sx={{ color: e.action === 'web_login_denied' ? C.coral : C.txt, fontSize: 12.5 }}>
                {ACT[e.action] || e.action}
                <span style={{ color: C.dim }}>{e.detail ? ' · ' + e.detail : ''}{e.ip ? ' · IP ' + e.ip : ''}{e.via ? ' · ' + (VIA[e.via] || e.via) : ''}</span>
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    );
  };

  const BODY = { links: Links, access: Access, preview: Preview, log: Log };
  return (
    <>
      <Box sx={{ display: 'flex', gap: .7, flexWrap: 'wrap', mb: 2 }}>
        {SUBS.map(([k, l, badge]) => (
          <Button key={k} size="small" data-guide={'cab-' + k} onClick={() => setSub(k)} variant={sub === k ? 'contained' : 'outlined'}
            sx={{ textTransform: 'none', borderRadius: 5, fontSize: 12.5, px: 1.6, borderColor: C.line,
                  color: sub === k ? '#04121a' : C.neonTxt, bgcolor: sub === k ? C.neon : 'transparent',
                  '&:hover': { borderColor: C.neon, bgcolor: sub === k ? C.neon : 'rgba(0,229,160,.08)' } }}>
            {l}{badge != null && badge !== 0 ? ` · ${badge}` : ''}
          </Button>
        ))}
      </Box>
      {BODY[sub]()}
    </>
  );
}
